/**
 * Invoice PDF Generator
 * Generează un PDF de factură folosind jsPDF, configurat după un preset de șablon.
 *
 * Aspectul (culori, aliniere antet, bandă, watermark) variază în funcție de
 * preset-ul activ pentru firmă (classic / modern / minimal / pro). Logo-ul
 * firmei (din settings.company_logo, dataURL base64) este randat în antet.
 */
import jsPDF from "jspdf";
import { addRobotoFont } from "@/utils/robotoFont";

const formatNumber = (num) => {
  if (num === undefined || num === null || isNaN(num)) return "0,00";
  return new Intl.NumberFormat("ro-RO", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(num);
};

// HEX → [r, g, b]
const hexToRgb = (hex) => {
  const m = String(hex || "").match(/^#?([a-f0-9]{6})$/i);
  if (!m) return [14, 116, 144];
  const v = m[1];
  return [parseInt(v.slice(0, 2), 16), parseInt(v.slice(2, 4), 16), parseInt(v.slice(4, 6), 16)];
};

// Detectează formatul imaginii dintr-un dataURL ("image/png" etc.)
const detectImageFormat = (dataUrl) => {
  if (!dataUrl || typeof dataUrl !== "string") return null;
  if (dataUrl.startsWith("data:image/png")) return "PNG";
  if (dataUrl.startsWith("data:image/jpeg") || dataUrl.startsWith("data:image/jpg")) return "JPEG";
  if (dataUrl.startsWith("data:image/webp")) return "WEBP";
  if (dataUrl.startsWith("data:image/gif")) return "GIF";
  return null;
};

const PRESETS = {
  classic: { accent: "#0e7490", title_align: "center", banner: "line", logo_pos: "left" },
  modern: { accent: "#be123c", title_align: "right", banner: "full", logo_pos: "left" },
  minimal: { accent: "#000000", title_align: "right", banner: "none", logo_pos: "left" },
  pro: { accent: "#4f46e5", title_align: "right", banner: "side", logo_pos: "left" },
};

export const generateInvoicePDF = (invoice, settings, presetOverride) => {
  const presetId = (presetOverride && presetOverride.preset_id) || settings?.invoice_template_preset || "classic";
  const cfg = { ...PRESETS[presetId] || PRESETS.classic };
  if (presetOverride && presetOverride.accent) cfg.accent = presetOverride.accent;
  const [ar, ag, ab] = hexToRgb(cfg.accent);

  const pdf = new jsPDF("p", "mm", "a4");
  try { addRobotoFont(pdf); pdf.setFont("Roboto"); } catch (e) {
    console.warn("Roboto font failed to load, using default helvetica:", e);
  }

  const pageWidth = pdf.internal.pageSize.getWidth();
  const margin = 15;
  let y = 18;

  // ── Bandă laterală (preset "pro") ──
  if (cfg.banner === "side") {
    pdf.setFillColor(ar, ag, ab);
    pdf.rect(0, 0, 6, pdf.internal.pageSize.getHeight(), "F");
  }

  // ── Bandă completă antet (preset "modern") ──
  if (cfg.banner === "full") {
    pdf.setFillColor(ar, ag, ab);
    pdf.rect(0, 0, pageWidth, 28, "F");
  }

  // ── LOGO firma în antet ──
  const logo = settings?.company_logo || settings?.logo_url || "";
  const logoFormat = detectImageFormat(logo);
  let logoBottomY = y;
  if (logo && logoFormat) {
    try {
      const logoX = margin;
      const logoY = cfg.banner === "full" ? 4 : y;
      pdf.addImage(logo, logoFormat, logoX, logoY, 32, 18, undefined, "FAST");
      logoBottomY = logoY + 20;
    } catch (e) {
      // logo image cannot be decoded → continuă fără
      console.warn("Failed to embed logo in invoice PDF:", e);
    }
  }

  // ── Titlu FACTURĂ ──
  const titleColor = cfg.banner === "full" ? [255, 255, 255] : [ar, ag, ab];
  pdf.setFontSize(24);
  pdf.setTextColor(...titleColor);
  const docTitle = (invoice.invoice_doc_type === "proforma") ? "PROFORMA" : "FACTURA";
  if (cfg.title_align === "center") {
    pdf.text(docTitle, pageWidth / 2, y + 4, { align: "center" });
  } else {
    pdf.text(docTitle, pageWidth - margin, y + 4, { align: "right" });
  }
  pdf.setFontSize(12);
  pdf.setTextColor(...(cfg.banner === "full" ? [255, 255, 255] : [60, 60, 60]));
  const subY = y + 12;
  const subTxt = `Seria ${invoice.series} Nr. ${String(invoice.number).padStart(4, "0")}  ·  ${invoice.invoice_date}`;
  if (cfg.title_align === "center") {
    pdf.text(subTxt, pageWidth / 2, subY, { align: "center" });
  } else {
    pdf.text(subTxt, pageWidth - margin, subY, { align: "right" });
  }
  if (invoice.due_date && cfg.banner !== "full") {
    pdf.setFontSize(9);
    pdf.setTextColor(180, 40, 40);
    const due = `Scadență: ${invoice.due_date}`;
    if (cfg.title_align === "center") pdf.text(due, pageWidth / 2, subY + 5, { align: "center" });
    else pdf.text(due, pageWidth - margin, subY + 5, { align: "right" });
  }

  y = Math.max(logoBottomY, subY + 8, cfg.banner === "full" ? 34 : 36);

  // Linie separator (banner === "line")
  if (cfg.banner === "line") {
    pdf.setDrawColor(ar, ag, ab);
    pdf.setLineWidth(0.7);
    pdf.line(margin, y, pageWidth - margin, y);
    pdf.setLineWidth(0.2);
    y += 6;
  } else {
    y += 4;
  }

  // ── FURNIZOR / CLIENT ──
  const colWidth = (pageWidth - margin * 3) / 2;
  pdf.setFontSize(10);
  pdf.setTextColor(ar, ag, ab);
  pdf.text("FURNIZOR", margin, y);
  pdf.text("CLIENT", margin + colWidth + margin, y);
  y += 6;

  pdf.setFontSize(9);
  pdf.setTextColor(40, 40, 40);
  let leftY = y;
  pdf.setFont(undefined, "bold");
  pdf.text(invoice.supplier_name || settings?.company_name || "", margin, leftY);
  leftY += 5;
  pdf.setFont(undefined, "normal");
  if (invoice.supplier_cui || settings?.company_cui) { pdf.text(`CUI: ${invoice.supplier_cui || settings?.company_cui}`, margin, leftY); leftY += 4; }
  if (invoice.supplier_reg_com || settings?.company_reg_com) { pdf.text(`Reg. Com.: ${invoice.supplier_reg_com || settings?.company_reg_com}`, margin, leftY); leftY += 4; }
  if (invoice.supplier_address || settings?.company_address) { pdf.text(`${invoice.supplier_address || settings?.company_address}`, margin, leftY, { maxWidth: colWidth }); leftY += 8; }
  if (invoice.supplier_iban || settings?.company_iban) { pdf.text(`IBAN: ${invoice.supplier_iban || settings?.company_iban}`, margin, leftY, { maxWidth: colWidth }); leftY += 4; }
  if (invoice.supplier_bank || settings?.company_bank) { pdf.text(`Banca: ${invoice.supplier_bank || settings?.company_bank}`, margin, leftY); leftY += 4; }

  let rightY = y;
  const rightX = margin + colWidth + margin;
  pdf.setFont(undefined, "bold");
  pdf.text(invoice.client_name || "", rightX, rightY);
  rightY += 5;
  pdf.setFont(undefined, "normal");
  if (invoice.client_cui) { pdf.text(`CUI: ${invoice.client_cui}`, rightX, rightY); rightY += 4; }
  if (invoice.client_reg_com) { pdf.text(`Reg. Com.: ${invoice.client_reg_com}`, rightX, rightY); rightY += 4; }
  if (invoice.client_address) { pdf.text(`${invoice.client_address}`, rightX, rightY, { maxWidth: colWidth }); }

  y = Math.max(leftY, rightY) + 10;

  // ── Tabel articole ──
  pdf.setDrawColor(200, 200, 200);
  const tableX = margin;
  const tableWidth = pageWidth - margin * 2;
  const colWidths = [70, 15, 20, 30, 15, 30];

  pdf.setFillColor(ar, ag, ab);
  pdf.rect(tableX, y, tableWidth, 8, "F");
  pdf.setFontSize(8);
  pdf.setTextColor(255, 255, 255);
  pdf.setFont(undefined, "bold");

  let tableColX = tableX + 2;
  pdf.text("Denumire produs/serviciu", tableColX, y + 5); tableColX += colWidths[0];
  pdf.text("U.M.", tableColX, y + 5); tableColX += colWidths[1];
  pdf.text("Cant.", tableColX, y + 5); tableColX += colWidths[2];
  pdf.text("Pret unit.", tableColX, y + 5); tableColX += colWidths[3];
  pdf.text("TVA", tableColX, y + 5); tableColX += colWidths[4];
  pdf.text("Total", tableColX, y + 5);

  y += 10;
  pdf.setFont(undefined, "normal");
  pdf.setTextColor(40, 40, 40);

  (invoice.items || []).forEach((item) => {
    if (y > 250) { pdf.addPage(); y = 20; }
    tableColX = tableX + 2;
    pdf.text(item.name?.substring(0, 45) || "", tableColX, y + 4, { maxWidth: colWidths[0] - 4 }); tableColX += colWidths[0];
    pdf.text(item.um || "buc", tableColX, y + 4); tableColX += colWidths[1];
    pdf.text(String(item.quantity || 1), tableColX, y + 4); tableColX += colWidths[2];
    pdf.text(formatNumber(item.unit_price || 0), tableColX, y + 4); tableColX += colWidths[3];
    pdf.text(`${item.vat_percent || 21}%`, tableColX, y + 4); tableColX += colWidths[4];
    pdf.text(formatNumber(item.total || 0), tableColX, y + 4);
    y += 7;
    pdf.setDrawColor(230, 230, 230);
    pdf.line(tableX, y, tableX + tableWidth, y);
  });

  y += 8;

  const totalsX = pageWidth - margin - 70;
  pdf.setFontSize(10);
  pdf.setTextColor(40, 40, 40);
  pdf.text("Subtotal:", totalsX, y);
  pdf.text(`${formatNumber(invoice.subtotal || 0)} RON`, totalsX + 50, y, { align: "right" }); y += 6;
  pdf.text("TVA:", totalsX, y);
  pdf.text(`${formatNumber(invoice.vat_amount || 0)} RON`, totalsX + 50, y, { align: "right" }); y += 8;

  // Total emphasized cu culoarea accentului
  pdf.setFillColor(ar, ag, ab);
  pdf.rect(totalsX - 4, y - 6, 78, 10, "F");
  pdf.setFont(undefined, "bold");
  pdf.setFontSize(12);
  pdf.setTextColor(255, 255, 255);
  pdf.text("TOTAL:", totalsX, y);
  pdf.text(`${formatNumber(invoice.total || 0)} RON`, totalsX + 50, y, { align: "right" });
  pdf.setFont(undefined, "normal");
  pdf.setTextColor(40, 40, 40);

  if (invoice.notes) {
    y += 14;
    pdf.setFontSize(9);
    pdf.text("Observații:", margin, y); y += 5;
    pdf.text(invoice.notes, margin, y, { maxWidth: pageWidth - margin * 2 });
  }

  y = pdf.internal.pageSize.getHeight() - 20;
  pdf.setFontSize(8);
  const invPrefix = "Factura generata cu ";
  const invLink = "e-Tesa";
  const wInvPrefix = pdf.getTextWidth(invPrefix);
  let invFooterX = pageWidth / 2 - (wInvPrefix + pdf.getTextWidth(invLink)) / 2;
  pdf.setTextColor(150, 150, 150);
  pdf.text(invPrefix, invFooterX, y);
  pdf.setTextColor(29, 78, 216);
  pdf.textWithLink(invLink, invFooterX + wInvPrefix, y, { url: "https://e-tesa.ro" });

  const invoiceDate = invoice.invoice_date || new Date().toISOString().split("T")[0];
  const dateParts = invoiceDate.split("-");
  const formattedDate = dateParts.length === 3
    ? `${dateParts[2]}.${dateParts[1]}.${dateParts[0]}`
    : invoiceDate;
  const companyName = (invoice.supplier_name || settings?.company_name || "Companie")
    .replace(/[^a-zA-Z0-9\s]/g, "")
    .trim()
    .replace(/\s+/g, "_");
  const fileName = `${invoice.series} ${String(invoice.number).padStart(3, "0")} ${formattedDate} ${companyName}.pdf`;
  pdf.save(fileName);
  return fileName;
};
